import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Stethoscope,
  Eye,
  ClipboardList,
  Pencil,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ipdApi } from '@/api/ipd';
import { IPDAdmission, IPDQueueEntry } from '@/types/ipd';
import { ensureDoctorPrefix } from '@/lib/doctorName';
import { isRmoRole } from '@/lib/clinicalRoles';
import ReceptionistDischargeForm from '@/components/ipd/ReceptionistDischargeForm';

export default function IPDManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRmo = isRmoRole(user?.role);

  const [activeTab, setActiveTab] = useState<'active' | 'discharged'>('active');
  const [admissions, setAdmissions] = useState<IPDAdmission[]>([]);
  const [dischargedEntries, setDischargedEntries] = useState<IPDQueueEntry[]>([]);
  const [filteredAdmissions, setFilteredAdmissions] = useState<IPDAdmission[]>([]);
  const [filteredDischarged, setFilteredDischarged] = useState<IPDQueueEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedWard, setSelectedWard] = useState<string>('ALL');

  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [selectedQueueEntry, setSelectedQueueEntry] = useState<IPDQueueEntry | null>(null);

  const fetchActive = useCallback(async () => {
    const response = await ipdApi.getAdmissions({ status: 'ADMITTED' });
    const list = response.data.data ?? [];
    const scoped = isRmo
      ? list
      : list.filter((a: IPDAdmission) => a.assignedDoctorId === user?.id);
    setAdmissions(scoped);
    setFilteredAdmissions(scoped);
  }, [isRmo, user?.id]);

  const fetchDischarged = useCallback(async () => {
    const response = await ipdApi.getQueue({ status: 'DISCHARGED' });
    const list = response.data.data ?? [];
    const scoped = isRmo
      ? list
      : list.filter((e: IPDQueueEntry) => e.admission?.assignedDoctor?.id === user?.id);
    setDischargedEntries(scoped);
    setFilteredDischarged(scoped);
  }, [isRmo, user?.id]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      if (activeTab === 'active') {
        await fetchActive();
      } else {
        await fetchDischarged();
      }
    } catch (error) {
      console.error('Error fetching IPD data:', error);
      toast.error('Failed to load IPD patients');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, fetchActive, fetchDischarged]);

  useEffect(() => {
    if (user?.id) load();
  }, [user?.id, load]);

  const filterByWard = (wardType: string) => {
    setSelectedWard(wardType);
    if (wardType === 'ALL') {
      setFilteredAdmissions(admissions);
      setFilteredDischarged(dischargedEntries);
      return;
    }
    setFilteredAdmissions(admissions.filter((a) => a.wardType === wardType));
    setFilteredDischarged(
      dischargedEntries.filter((e) => e.admission?.wardType === wardType)
    );
  };

  const handleOpenDischargeSummaryPdf = async (entry: IPDQueueEntry) => {
    const admissionId = entry.admission?.id;
    if (!admissionId) {
      toast.error('Admission not found');
      return;
    }
    try {
      const res = await ipdApi.getDischargeSummary(admissionId);
      const summary = res.data?.data;
      if (!summary) {
        toast.error('No discharge summary on file');
        return;
      }
      const dischargeDateIso =
        typeof summary.dischargeDate === 'string'
          ? summary.dischargeDate
          : new Date(summary.dischargeDate as unknown as Date).toISOString();
      const pdfRes = await ipdApi.generateDischargeSummaryPDF(admissionId, {
        dischargeDate: dischargeDateIso,
        finalDiagnosis: summary.finalDiagnosis,
        treatmentSummary: summary.treatmentSummary,
        proceduresPerformed: summary.proceduresPerformed ?? '',
        medicationsPrescribed: summary.medicationsPrescribed,
        followUpInstructions: summary.followUpInstructions,
        followUpAt: summary.followUpAt ?? null,
        followUpReason: summary.followUpReason ?? null,
        remarks: summary.remarks ?? '',
        doctorSignature: summary.doctorSignature ?? '',
        hospitalStamp: summary.hospitalStamp ?? '',
      });
      const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) toast.error('Please allow pop-ups to view the PDF');
      else toast.success('Opening discharge summary');
    } catch {
      toast.error('Failed to open discharge summary');
    }
  };

  // The discharge-summary form expects an IPDQueueEntry with the admission attached.
  // Admitted rows come from getAdmissions(), whose queue payload may not nest the admission, so attach it here.
  const buildQueueEntryForDischarge = (admission: IPDAdmission): IPDQueueEntry =>
    ({
      ...admission.queue,
      status: admission.queue?.status ?? admission.status,
      admission: { ...(admission.queue?.admission ?? {}), ...admission },
    }) as unknown as IPDQueueEntry;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const getWardTypeColor = (type: string) => {
    switch (type) {
      case 'ICU':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'PRIVATE':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'SEMI_PRIVATE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'EMERGENCY':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const pageTitle = isRmo ? 'All IPD Patients' : 'My IPD Patients';
  const pageSubtitle = isRmo
    ? 'Hospital-wide admitted and discharged inpatients · edit discharge summaries'
    : `Patients assigned to ${ensureDoctorPrefix(user?.name)}`;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
        <Card>
          <CardContent className="flex items-center justify-center gap-3 p-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-600 border-t-transparent" />
            <span className="text-slate-600">Loading…</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  const wardFilter = (
    <Select value={selectedWard} onValueChange={filterByWard}>
      <SelectTrigger className="w-48">
        <SelectValue placeholder="Filter by ward" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">All Wards</SelectItem>
        <SelectItem value="ICU">ICU</SelectItem>
        <SelectItem value="PRIVATE">Private</SelectItem>
        <SelectItem value="SEMI_PRIVATE">Semi-Private</SelectItem>
        <SelectItem value="GENERAL">General</SelectItem>
        <SelectItem value="EMERGENCY">Emergency</SelectItem>
      </SelectContent>
    </Select>
  );

  const activeTable = (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Stethoscope className="h-5 w-5 text-teal-600" />
          {isRmo ? 'Currently admitted' : 'My assigned patients'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {filteredAdmissions.length === 0 ? (
          <div className="py-10 text-center text-slate-500">
            {isRmo ? 'No patients currently admitted' : 'No patients assigned to you in IPD'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  {isRmo && <TableHead>Consultant</TableHead>}
                  <TableHead>Ward</TableHead>
                  <TableHead>Room/Bed</TableHead>
                  <TableHead>Admitted</TableHead>
                  <TableHead>Visits</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAdmissions.map((admission) => (
                  <TableRow key={admission.id}>
                    <TableCell>
                      <div className="font-medium">{admission.queue?.patient?.name}</div>
                      <div className="text-sm text-slate-500">{admission.queue?.ipdNumber}</div>
                    </TableCell>
                    {isRmo && (
                      <TableCell className="text-sm">
                        {ensureDoctorPrefix(admission.assignedDoctor?.name)}
                        {admission.assignedDoctor?.specialisation && (
                          <div className="text-xs text-slate-500">
                            {admission.assignedDoctor.specialisation}
                          </div>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {admission.thirdPartyHospitalName ? (
                        <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 border border-amber-200">
                          Third party: {admission.thirdPartyHospitalName}
                        </span>
                      ) : (
                        <Badge className={getWardTypeColor(admission.wardType)}>{admission.wardType}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {admission.thirdPartyHospitalName
                        ? '—'
                        : `${admission.roomNumber ?? '—'} / ${admission.bedNumber ?? '—'}`}
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(admission.admissionDate)}</TableCell>
                    <TableCell className="text-sm">{admission.visits?.length ?? 0}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          className="bg-teal-700 hover:bg-teal-800"
                          onClick={() => navigate(`/doctor/ipd/${admission.id}`)}
                        >
                          <Eye className="mr-1 h-4 w-4" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedQueueEntry(buildQueueEntryForDischarge(admission));
                            setDischargeDialogOpen(true);
                          }}
                        >
                          <Pencil className="mr-1 h-4 w-4" />
                          {admission.dischargeSummary?.id ? 'Edit discharge summary' : 'Write discharge summary'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const dischargedTable = (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardList className="h-5 w-5 text-violet-600" />
          Discharged patients
        </CardTitle>
      </CardHeader>
      <CardContent>
        {filteredDischarged.length === 0 ? (
          <div className="py-10 text-center text-slate-500">No discharged patients found</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>IPD No.</TableHead>
                  {isRmo && <TableHead>Consultant</TableHead>}
                  <TableHead>Discharged</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDischarged.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">{entry.patient?.name}</TableCell>
                    <TableCell>{entry.ipdNumber ?? '—'}</TableCell>
                    {isRmo && (
                      <TableCell className="text-sm">
                        {ensureDoctorPrefix(entry.admission?.assignedDoctor?.name)}
                      </TableCell>
                    )}
                    <TableCell className="text-sm">
                      {entry.admission?.dischargeDate
                        ? formatDate(entry.admission.dischargeDate)
                        : entry.dischargedAt
                          ? formatDate(entry.dischargedAt)
                          : '—'}
                    </TableCell>
                    <TableCell>
                      {entry.admission?.dischargeSummary?.id ? (
                        <Badge variant="secondary" className="bg-emerald-50 text-emerald-800">
                          On file
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-700">
                          Missing
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {entry.admission?.dischargeSummary?.id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenDischargeSummaryPdf(entry)}
                          >
                            <Download className="mr-1 h-3.5 w-3.5" />
                            PDF
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedQueueEntry(entry);
                            setDischargeDialogOpen(true);
                          }}
                        >
                          <Pencil className="mr-1 h-3.5 w-3.5" />
                          {entry.admission?.dischargeSummary?.id ? 'Edit summary' : 'Add summary'}
                        </Button>
                        {entry.admission?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/doctor/ipd/${entry.admission!.id}`)}
                          >
                            <Eye className="mr-1 h-3.5 w-3.5" />
                            Chart
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="thms-page-header">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
          <p className="mt-1 text-sm text-slate-500">{pageSubtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">
            {activeTab === 'active'
              ? `${filteredAdmissions.length} admitted`
              : `${filteredDischarged.length} discharged`}
          </span>
          {wardFilter}
        </div>
      </div>

      {isRmo ? (
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'active' | 'discharged')}
        >
          <TabsList>
            <TabsTrigger value="active">Current IPD</TabsTrigger>
            <TabsTrigger value="discharged">Discharged & summaries</TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="mt-4">
            {activeTable}
          </TabsContent>
          <TabsContent value="discharged" className="mt-4">
            {dischargedTable}
          </TabsContent>
        </Tabs>
      ) : (
        activeTable
      )}

      <ReceptionistDischargeForm
        isOpen={dischargeDialogOpen}
        onClose={() => {
          setDischargeDialogOpen(false);
          setSelectedQueueEntry(null);
        }}
        onSuccess={() => {
          setDischargeDialogOpen(false);
          setSelectedQueueEntry(null);
          if (activeTab === 'discharged') fetchDischarged();
          else fetchActive();
        }}
        queueEntry={selectedQueueEntry}
      />
    </div>
  );
}
